import { Form, Row, Col, Input, Button, Descriptions } from 'antd';
import styles from './Description.module.less';
import FormItem from 'components/form/FormItem';
import api, { DLCType } from 'api';
import { FormProvider, useForm } from 'react-hook-form';
import { TFunction, useTranslation } from 'react-i18next';
import cx from 'classnames';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation } from 'react-router-dom';
import OmniNotification from 'components/OmniNotification';
import { isEmpty } from 'lodash';
import { DLCTabName, IDownloadableContentDetailDto } from './EditDownloadableContent';

const getValidationSchema = (t: TFunction) =>
	yup.object().shape({
		name: yup
			.string()
			.nullable()
			.required(
				t('validation.required', {
					fieldName: t('downloadableContentEdit.tabs.description.form.name.title'),
				}),
			)
			.max(
				80,
				t('validation.maxLength', {
					fieldName: t('downloadableContentEdit.tabs.description.form.name.title'),
					count: 80,
				}),
			),
		brief: yup
			.string()
			.nullable()
			.required(
				t('validation.required', {
					fieldName: t('downloadableContentEdit.tabs.description.form.brief.title'),
				}),
			)
			.max(
				200,
				t('validation.maxLength', {
					fieldName: t('downloadableContentEdit.tabs.description.form.brief.title'),
					count: 200,
				}),
			),
		description: yup
			.string()
			.nullable()
			.required(
				t('validation.required', {
					fieldName: t('downloadableContentEdit.tabs.description.form.description.title'),
				}),
			)
			.max(
				200,
				t('validation.maxLength', {
					fieldName: t('downloadableContentEdit.tabs.description.form.description.title'),
					count: 200,
				}),
			),
	});

type Props = {
	id: number;
	data: IDownloadableContentDetailDto;
	onSave: (number: string) => void;
};

interface FormValues {
	name: string;
	brief: string;
	description: string;
}

export interface DescriptionRef {
	validateDescriptionTab: () => void;
}

export default forwardRef(function Description(props: Props, ref: any) {
	const { t } = useTranslation();
	useLocation();
	const { id, data, onSave } = props;
	const validationSchema = useMemo(() => getValidationSchema(t), [t]);
	const [timesOfLoadData, setTimesOfLoadData] = useState<number>(0);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const methods = useForm<FormValues>({
		mode: 'all',
		defaultValues: {},
		resolver: yupResolver(validationSchema),
	});

	const {
		control,
		handleSubmit,
		reset,
		formState: { isDirty },
		trigger,
	} = methods;

	useImperativeHandle(ref, () => ({
		async validateDescriptionTab() {
			return await trigger();
		},
	}));

	const submitEvent = () => {
		handleSubmit(async (values: FormValues) => {
			setIsSubmitting(true);
			api
				.updateDescriptionForDLC({
					id: id,
					name: values.name,
					brief: values.brief,
					description: values.description,
				})
				.then(() => {
					OmniNotification.success({
						message: t('downloadableContentEdit.message.update.success.title'),
						description: t('downloadableContentEdit.message.update.success.description'),
					});
					onSave(DLCTabName.Description);
				})
				.catch(() => {
					OmniNotification.error({
						message: t('notification.error.title'),
						description: t('api.defaultError'),
					});
					setIsSubmitting(false);
				});
		})();
	};

	useEffect(() => {
		if (isSubmitting || timesOfLoadData == 0) {
			reset({
				name: data.name,
				brief: data.brief,
				description: data.description,
			});
		}

		if (!isEmpty(data) && (!data.tabName || data.tabName == DLCTabName.Description)) {
			setTimesOfLoadData(timesOfLoadData + 1);
		}
	}, [data]);

	useEffect(() => {
		if (timesOfLoadData > 1) {
			setIsSubmitting(false);
		}
	}, [timesOfLoadData]);

	const getTypeText = (t: TFunction, type?: DLCType) => {
		switch (type) {
			case DLCType.Durable:
				return t('downloadableContent.enmu.type.durable');
			case DLCType.Consumable:
				return t('downloadableContent.enmu.type.consumable');
			default:
				return '';
		}
	};

	return (
		<>
			<FormProvider {...methods}>
				<Form data-testid="form" layout="vertical" onFinish={submitEvent}>
					<Row>
						<div style={{ flexDirection: 'row', alignItems: 'center' }}>
							<span className={styles.title}>{t('downloadableContentEdit.tabs.description.title')}</span>
							<span className={styles.description}>{t('downloadableContentEdit.tabs.description.description')}</span>
						</div>
					</Row>
					<Row style={{ marginTop: 24 }}>
						<Col span={10}>
							<FormItem
								name="name"
								label={t('downloadableContentEdit.tabs.description.form.name.title')}
								control={control}
								className={cx('has-counter')}
								render={({ field }) => (
									<>
										<Input data-testid="name" {...field} />
										<span className="form-counter">{field.value?.length || 0}/80 characters</span>
									</>
								)}
							/>
						</Col>
					</Row>
					<Row>
						<Col>
							<Descriptions column={1}>
								<Descriptions.Item label={t('downloadableContent.table.columns.sku')}>{data.sku}</Descriptions.Item>
								<Descriptions.Item label={t('downloadableContent.table.columns.type')}>
									{getTypeText(t, data.type)}
								</Descriptions.Item>
							</Descriptions>
						</Col>
					</Row>
					<Row gutter={[16, 16]}>
						<Col span={12}>
							<FormItem
								name="brief"
								label={t('downloadableContentEdit.tabs.description.form.brief.title')}
								control={control}
								className={cx('has-counter')}
								hint={t('downloadableContentEdit.tabs.description.form.brief.description')}
								render={({ field }) => (
									<>
										<Input.TextArea data-testid="brief" size="large" rows={4} {...field} />
										<span className="form-counter">{field.value?.length || 0}/200 characters</span>
									</>
								)}
							/>
						</Col>
						<Col span={12}>
							<FormItem
								name="description"
								label={t('downloadableContentEdit.tabs.description.form.description.title')}
								control={control}
								className={cx('has-counter')}
								hint={t('downloadableContentEdit.tabs.description.form.description.description')}
								render={({ field }) => (
									<>
										<Input.TextArea data-testid="desription" size="large" rows={4} {...field} />
										<span className="form-counter">{field.value?.length || 0}/200 characters</span>
									</>
								)}
							/>
						</Col>
					</Row>
					<Button
						data-testid="description-tab-save-btn"
						loading={isSubmitting}
						type="primary"
						htmlType="submit"
						size="large"
						disabled={timesOfLoadData > 1 && (!isDirty || isSubmitting)}
						style={{ marginTop: 24 }}
					>
						{t('common.Save')}
					</Button>
				</Form>
			</FormProvider>
		</>
	);
});
